import _ from 'lodash'
import dayjs from 'dayjs'
import React, { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTable, CellProps, Column, Cell, Row, HeaderGroup, ColumnInstance } from 'react-table'

import Box from '@mui/material/Box'
import Chip, { ChipProps } from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableCell, { TableCellProps } from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'

import CastRoundedIcon from '@mui/icons-material/CastRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import GroupsIcon from '@mui/icons-material/Groups'
import LaunchIcon from '@mui/icons-material/Launch'

// import { ReactComponent as MedicalEquipmentIcon } from '@/assets/svg/medical-equipment.svg'

import CalendarIcon from '@/components/ui/icons/Calendar'
import ChipButton from '@/components/ui/buttons/ChipButton'
import DoctorIcon from '@/components/ui/icons/Doctor'
import PatientIcon from '@/components/ui/icons/Patient'
import Pagination from '@/components/ui/pagination/Pagination'

import { ConsultationType } from '@/core/models/consultation.model'
import { getStatusColor, redirectTo } from '@/utils/consultations'
import ConsultationsCardView from './ConsultationsCardView'
import { useMediaQuery, useTheme } from '@mui/material'
import PdsIcon from '@/components/ui/icons/Pds'
import { AmericanFormattedDate } from '@/components/ui/date/AmericanFormattedDate'
import { useIsUS } from '@/hooks/useIsUS'

export type ConsultationData = {
  id: string
  doctor?: {
    gender?: 'M' | 'F' | 'I' | 'O'
    name: string
    surname: string
  }
  patient: {
    gender?: 'M' | 'F' | 'I' | 'O'
    name: string
    surname: string
  }
  pds?: {
    gender?: 'M' | 'F' | 'I' | 'O'
    name: string
    surname: string
  }
  showButtons?: {
    accept?: boolean
    live?: boolean
    refuse?: boolean
  }
  startDate: string
  status: string
  type: string
  groupId?: string
}

type ConsultationsTableProps = {
  data: ConsultationData[]
  isDoctor?: boolean
  onAcceptConsultation?: (consultationId: string) => void
  onRefuseConsultation?: (consultationId: string) => void
  onRowsPerPageChange?: (rowsPerPage: number) => void
  onPageChange?: (newPage: number) => void
  rowsPerPageOptions?: number[]
  totalCount: number
}

const defaultRowsPerPageOptions = [5, 10, 15]

export default function ConsultationsTable(props: Readonly<ConsultationsTableProps>) {
  const {
    data,
    isDoctor = false,
    onAcceptConsultation,
    onRefuseConsultation,
    onPageChange,
    onRowsPerPageChange,
    rowsPerPageOptions = defaultRowsPerPageOptions,
    totalCount,
  } = props
  const navigate = useNavigate()
  const { t } = useTranslation()
  const theme = useTheme()
  const isUS = useIsUS()

  const columns = useMemo(() => {
    return [
      {
        Header: t(`page.consultations.table.header.${isDoctor ? 'pds' : 'doctor'}`),
        accessor: isDoctor ? 'pds' : 'doctor',
        Cell: ({ value: staff, row: { original: consultation } }: CellProps<ConsultationData, ConsultationData['doctor']>) => (
          <Stack direction="row" spacing={1} alignItems="center">
            {!!staff ? (
              <>
                {isDoctor ? <PdsIcon isFemale={staff.gender === 'F'} /> : <DoctorIcon isFemale={staff.gender === 'F'} />}
                <span>{_.trim(`${staff.name || ''} ${staff.surname || ''}`)}</span>
              </>
            ) : consultation.type === ConsultationType.Local ? (
              <>
                {/* <MedicalEquipmentIcon style={{ width: '24px', height: '24px' }} /> */}
                <Box component="span" sx={{ textTransform: 'capitalize' }}>
                  {t('page.consultations.toSend')}
                </Box>
              </>
            ) : (
              <span>-</span>
            )}
          </Stack>
        ),
      },
      {
        Header: t('page.consultations.table.header.patient'),
        accessor: 'patient',
        Cell: ({ value: patient, row: { original: consultation } }: CellProps<ConsultationData, ConsultationData['patient']>) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <PatientIcon isFemale={patient?.gender === 'F'} />
            <span>{_.trim(`${patient?.name || ''} ${patient?.surname || ''}`)}</span>
            {consultation.groupId && <GroupsIcon color="primary" fontSize="small" titleAccess={t('page.groupConsultation.title')} />}
          </Stack>
        ),
      },
      {
        Header: t('page.consultations.table.header.startDate'),
        accessor: 'startDate',
        Cell: ({ value }: CellProps<ConsultationData, ConsultationData['startDate']>) => (
          <Stack alignItems="center" justifyContent="start" direction="row" spacing={1}>
            <CalendarIcon fontSize="small" />
            <Chip
              color="secondary"
              label={
                isUS ? (
                  <AmericanFormattedDate date={value} />
                ) : (
                  <span>
                    {dayjs(value).isSame(dayjs(), 'day') ? t(`moment:calendar:today`) : dayjs(value).format('DD/MM/YYYY')} -{' '}
                    <b>{dayjs(value).format('HH[h]mm')}</b>
                  </span>
                )
              }
              variant="filled"
            />
          </Stack>
        ),
      },
      {
        align: 'center',
        Header: t('page.consultations.table.header.status'),
        accessor: 'status',
        Cell: ({ value: status }: CellProps<ConsultationData, ConsultationData['status']>) => {
          return <Chip color={getStatusColor(status)} label={t(`common:model.consultation.status.${status}.label`)} size="small" variant="filled" />
        },
      },
    ] as Column<ConsultationData>[]
  }, [isDoctor, t])

  const { getTableBodyProps, headerGroups, prepareRow, rows } = useTable<ConsultationData>({
    columns,
    data,
  })

  const [page, setPage] = useState<number>(1)

  const handleChangePage = (newPage: number) => {
    if (page === newPage) return
    setPage(newPage)
    onPageChange?.(newPage)
  }

  const handleAcceptConsultation = (row: Row<ConsultationData>) => () => {
    onAcceptConsultation?.(row.original.id)
  }

  const handleRefuseConsultation = (row: Row<ConsultationData>) => () => {
    onRefuseConsultation?.(row.original.id)
  }

  const handleOpenConsultationLive = (consultation: ConsultationData) => {
    if (consultation.groupId) {
      navigate(`/consultations/${consultation.id}/group-live?${searchParams.toString()}`)
      return
    }
    navigate(
      redirectTo({
        _id: consultation.id,
        status: consultation.status,
        type: consultation.type,
      }) + `?${searchParams.toString()}`
    )
  }

  const [searchParams] = useSearchParams()

  const handleOpenConsultationDetails = (row: Row<ConsultationData>) => () => {
    navigate(`/consultations/${row.original.id}?${searchParams.toString()}`)
  }

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <>
      {!isMobile ? (
        <TableContainer component={Paper} id="consultations-table">
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              {headerGroups.map((headerGroup: HeaderGroup<ConsultationData>) => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column: HeaderGroup<ConsultationData> & { align?: TableCellProps['align'] }) => {
                    return (
                      <TableCell {...column.getHeaderProps()} scope="col" align={column.align}>
                        {column.render('Header')}
                      </TableCell>
                    )
                  })}
                  <TableCell align="right">{t('common:table.header.action')}</TableCell>
                </TableRow>
              ))}
            </TableHead>
            <TableBody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row)
                return (
                  <TableRow {...row.getRowProps()} key={row.original.id}>
                    {row.cells.map((cell: Cell<ConsultationData>) => (
                      <TableCell {...cell.getCellProps()} align={(cell.column as ColumnInstance<ConsultationData> & { align?: TableCellProps['align'] }).align}>
                        {cell.render('Cell')}
                      </TableCell>
                    ))}
                    <TableCell key={`cell_${row.index}_actions`} align="right">
                      <Stack justifyContent="flex-end" direction="row" spacing={1}>
                        {!!row.original.showButtons?.accept && (
                          <Tooltip title={t('common:button.refuse.tooltip')}>
                            <ChipButton color="error" onClick={handleRefuseConsultation(row)}>
                              <ClearRoundedIcon />
                            </ChipButton>
                          </Tooltip>
                        )}
                        {!!row.original.showButtons?.refuse && (
                          <Tooltip title={t('common:button.accept.tooltip')}>
                            <ChipButton color="success" onClick={handleAcceptConsultation(row)}>
                              <CheckRoundedIcon />
                            </ChipButton>
                          </Tooltip>
                        )}
                        {!!row.original.showButtons?.live && (
                          <Tooltip title={t('page.consultations.openConsultationLiveButton.tooltip')}>
                            <ChipButton id="see" color="info" onClick={() => handleOpenConsultationLive(row.original)}>
                              <CastRoundedIcon />
                            </ChipButton>
                          </Tooltip>
                        )}
                        <Tooltip title={t('page.consultations.openConsultationDetailsButton.tooltip')}>
                          <ChipButton color="info" onClick={handleOpenConsultationDetails(row)}>
                            <LaunchIcon />
                          </ChipButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>

          <Box sx={{ display: 'flex', justifyContent: 'end', p: '10px' }}>
            <Pagination
              color="primary"
              itemsPerPageOptions={rowsPerPageOptions}
              shape="rounded"
              page={page}
              onPageChange={handleChangePage}
              onItemsPerPageChange={onRowsPerPageChange}
              totalItems={totalCount}
            />
          </Box>
        </TableContainer>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'end', p: '10px' }}>
            <Pagination
              color="primary"
              itemsPerPageOptions={rowsPerPageOptions}
              shape="rounded"
              page={page}
              onPageChange={handleChangePage}
              onItemsPerPageChange={onRowsPerPageChange}
              totalItems={totalCount}
            />
          </Box>
          <ConsultationsCardView
            data={data}
            onAcceptConsultation={(consultationId) => (onAcceptConsultation ? consultationId : {})}
            onRefuseConsultation={(consultationId) => (onRefuseConsultation ? consultationId : {})}
            onOpenConsultationLive={(data) => handleOpenConsultationLive(data)}
            onOpenConsultationDetails={(consultation) => {
              navigate(`/consultations/${consultation.id}`)
            }}
          />
        </Box>
      )}
    </>
  )
}
